import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Select } from '@/shared/components/ui/Select';
import { useAgingExceptions } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { AgingExceptionsFilters } from '../services/reportsService';

interface AgingExceptionsSectionProps {
  locationIds?: string[];
  manufacturerIds?: string[];
}

export function AgingExceptionsSection({
  locationIds,
  manufacturerIds,
}: AgingExceptionsSectionProps) {
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'ORDERED' | 'BOTH'>(
    'BOTH'
  );
  const [minDays, setMinDays] = useState(7);

  const filters: AgingExceptionsFilters = {
    location_ids: locationIds,
    manufacturer_ids: manufacturerIds,
    status: statusFilter,
    min_days: minDays,
  };

  const { data, isLoading, error } = useAgingExceptions(filters);

  const getAgingColor = (days: number): 'success' | 'warning' | 'danger' => {
    if (days <= 7) return 'success';
    if (days <= 14) return 'warning';
    return 'danger';
  };

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        Location: item.location_name,
        Manufacturer: item.manufacturer_name,
        Brand: item.brand,
        'Frame Model': item.frame_name_or_model,
        Color: item.color,
        Size: item.eye_size,
        'Order Type': item.order_type,
        'Patient Name': item.patient_name || '',
        'Tray Number': item.tray_number || '',
        Status: item.status,
        'Entry Date': item.reorder_entry_date,
        'Ordered Date': item.ordered_date || '',
        'Days Aging': item.days_aging,
        'PO Number': item.po_number || '',
      })),
      'aging-exceptions-report'
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Aging & Exceptions</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Aging & Exceptions</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Aging & Exceptions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing all current exceptions (not date-filtered)
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Local Filters */}
      <div className="flex gap-4 mb-4">
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <Select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as 'PENDING' | 'ORDERED' | 'BOTH')
            }
            options={[
              { value: 'BOTH', label: 'Both Pending & Ordered' },
              { value: 'PENDING', label: 'Pending Only' },
              { value: 'ORDERED', label: 'Ordered Only' },
            ]}
          />
        </div>

        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Days
          </label>
          <Select
            value={minDays.toString()}
            onChange={(e) => setMinDays(parseInt(e.target.value))}
            options={[
              { value: '0', label: 'All' },
              { value: '5', label: '5+ days' },
              { value: '7', label: '7+ days' },
              { value: '14', label: '14+ days' },
              { value: '30', label: '30+ days' },
            ]}
          />
        </div>
      </div>

      {!data || data.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No aging exceptions found. Great job! 🎉
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 border-2 border-red-200 bg-red-50">
              <div className="text-sm text-gray-600">Critical (14+ days)</div>
              <div className="text-2xl font-bold text-red-600">
                {data.filter((item) => item.days_aging >= 14).length}
              </div>
            </Card>
            <Card className="p-4 border-2 border-yellow-200 bg-yellow-50">
              <div className="text-sm text-gray-600">Warning (7-13 days)</div>
              <div className="text-2xl font-bold text-yellow-600">
                {data.filter((item) => item.days_aging >= 7 && item.days_aging < 14).length}
              </div>
            </Card>
            <Card className="p-4 border-2 border-gray-200">
              <div className="text-sm text-gray-600">Total Exceptions</div>
              <div className="text-2xl font-bold">{data.length}</div>
            </Card>
          </div>

          {/* Exceptions Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Days
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Manufacturer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Frame Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Entry Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    PO#
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={getAgingColor(item.days_aging)}>
                        {item.days_aging} days
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={item.status === 'PENDING' ? 'warning' : 'info'}>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className="hidden lg:inline">{item.location_name}</span>
                      <span className="lg:hidden">{item.location_short_code || item.location_name}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      {item.manufacturer_name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="max-w-xs">
                        <div className="font-medium">{item.brand} - {item.frame_name_or_model}</div>
                        <div className="text-gray-500">
                          {item.color} | Size {item.eye_size}
                        </div>
                        {item.patient_name && (
                          <div className="text-gray-600 text-xs">
                            Patient: {item.patient_name} (Tray {item.tray_number})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <Badge variant={item.order_type === 'PATIENT' ? 'warning' : 'neutral'}>
                        {item.order_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {formatDate(item.reorder_entry_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.po_number || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            Showing {data.length} items requiring attention
          </div>
        </>
      )}
    </Card>
  );
}
